'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetPositionsRow } from '@morfi/backend-sdk-v2/client';
import { isI64 } from '@morfi/market-math';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OutcomeIdentityImage } from '@/components/market/identity/outcome-identity-image';
import { AnimatedBigInt } from '@/components/ui/animated-bigint';
import { Skeleton } from '@/components/ui/app-skeleton';
import { DataListItem, type DataListItemMetric } from '@/components/ui/data-list-item';
import { onEvent } from '@/events';
import { useMarketOutcomeColor } from '@/hooks/chart/use-outcome-color';
import type { UserPortfolioPositionDisplayRow } from '@/hooks/positions/user-portfolio/display-types';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { useFormattedCurrency } from '@/lib/currency';
import { pnlColorClass } from '@/lib/market/position-pnl';
import { useMarketDetailPrefetchIntent } from '@/lib/sdk/prefetch';
import { DURATION_POSITION_SMALL_MS } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import { PortfolioPositionShareButton } from './portfolio-position-actions';
import { PortfolioPositionResultBadge } from './portfolio-position-result-badge';
type PositionListItemProps = {
  row: UserPortfolioPositionDisplayRow;
  mode: 'open';
  isOwnPortfolio: boolean;
} | {
  row: GetPositionsRow;
  mode: 'closed';
  isOwnPortfolio: boolean;
  claimReady?: boolean;
};
export function PortfolioPositionListItem(props) {
  const $ = _c(42);
  const {
    row,
    mode,
    isOwnPortfolio
  } = props;
  const t = useTranslations("profile.stats.positions");
  const tFee = useTranslations("trade.fees");
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  const label = row.outcomeLabel ?? "";
  const color = useMarketOutcomeColor(row.market, row.outcomeIndex);
  const winner = row.positionType === "winner";
  const href = winner ? `/winners/${row.outcomeMint}` : `/markets/${row.market}`;
  const intent = useIntentRoutePrefetch(href, useMarketDetailPrefetchIntent(winner ? null : row.market).onMouseEnter);
  let t0;
  if ($[0] !== color.hue || $[1] !== color.text || $[2] !== formatD6 || $[3] !== formatD6Signed || $[4] !== href || $[5] !== intent || $[6] !== isOwnPortfolio || $[7] !== label || $[8] !== mode || $[9] !== props || $[10] !== row.market || $[11] !== row.marketTitle || $[12] !== row.outcomeImageNsfw || $[13] !== row.outcomeImageUrl || $[14] !== row.outcomeIndex || $[15] !== row.realizedPnlD6 || $[16] !== row.resultState || $[17] !== row.wallet || $[18] !== t || $[19] !== tFee) {
    const {
      realised,
      value,
      unrealised,
      invested,
      penalty
    } = positionAmounts(props);
    const currentValueMetric = {
      key: "value",
      label: t("value"),
      value: amount(value, formatD6)
    };
    const realisedMetric = {
      key: "realised",
      label: t("realised"),
      value: amount(realised, formatD6Signed, true)
    };
    const metrics = [{
      key: "invested",
      label: t("invested"),
      value: amount(invested, formatD6)
    }, ...(mode === "open" ? [currentValueMetric, {
      key: "unrealised",
      label: t("unrealised"),
      value: amount(unrealised, formatD6Signed, true)
    }] : []), realisedMetric, ...(penalty !== null && penalty > BigInt(0) ? [{
      key: "penalty",
      label: tFee("penalty_fee"),
      value: amount(penalty, formatD6)
    }] : [])];
    let t1;
    if ($[21] !== isOwnPortfolio || $[22] !== row.market || $[23] !== row.outcomeIndex) {
      t1 = function recordClick() {
        if (isOwnPortfolio) {
          onEvent("portfolio_position_sell_clicked", {
            marketPubkey: row.market,
            outcomeIndex: row.outcomeIndex
          });
        }
      };
      $[21] = isOwnPortfolio;
      $[22] = row.market;
      $[23] = row.outcomeIndex;
      $[24] = t1;
    } else {
      t1 = $[24];
    }
    const recordClick = t1;
    let t2;
    if ($[25] !== label) {
      t2 = <span className="truncate">{label}</span>;
      $[25] = label;
      $[26] = t2;
    } else {
      t2 = $[26];
    }
    let t3;
    if ($[27] !== row.resultState) {
      t3 = <PortfolioPositionResultBadge resultState={row.resultState} />;
      $[27] = row.resultState;
      $[28] = t3;
    } else {
      t3 = $[28];
    }
    let t4;
    if ($[29] !== t2 || $[30] !== t3) {
      t4 = <span className="flex min-w-0 items-center gap-2">{t2}{t3}</span>;
      $[29] = t2;
      $[30] = t3;
      $[31] = t4;
    } else {
      t4 = $[31];
    }
    let t5;
    if ($[32] !== color.hue || $[33] !== row.market || $[34] !== row.outcomeImageNsfw || $[35] !== row.outcomeImageUrl || $[36] !== row.outcomeIndex) {
      t5 = <OutcomeIdentityImage variant="thumbnail" entity={{
        kind: "outcome",
        marketPubkey: row.market,
        outcomeIndex: row.outcomeIndex,
        hue: color.hue,
        imageUrl: row.outcomeImageUrl,
        nsfw: row.outcomeImageNsfw
      }} alt="" size="size-12" rounded="rounded-control" preview={false} />;
      $[32] = color.hue;
      $[33] = row.market;
      $[34] = row.outcomeImageNsfw;
      $[35] = row.outcomeImageUrl;
      $[36] = row.outcomeIndex;
      $[37] = t5;
    } else {
      t5 = $[37];
    }
    let t6;
    if ($[38] !== color.text) {
      t6 = cn("size-3 rounded-full bg-current", color.text);
      $[38] = color.text;
      $[39] = t6;
    } else {
      t6 = $[39];
    }
    let t7;
    if ($[40] !== t6) {
      t7 = <span className={t6} />;
      $[40] = t6;
      $[41] = t7;
    } else {
      t7 = $[41];
    }
    t0 = <DataListItem testId="portfolio-position-list-item" href={href} intent={intent} onClick={recordClick} ariaLabel={`${label} · ${row.marketTitle ?? row.market}`} primaryText={t4} secondaryText={row.marketTitle ?? row.market} image={t5} imageBadge={t7} metrics={metrics} compactMetric={mode === "open" ? currentValueMetric : realisedMetric} action={row.wallet.length > 0 ? <PortfolioPositionShareButton destinationPath={href} marketPubkey={row.market} marketTitle={row.marketTitle ?? row.market} walletPubkey={row.wallet} outcomeIndex={row.outcomeIndex} outcomeTitle={label} closed={mode === "closed"} heroPnlD6={mode === "open" ? unrealised?.toString() : row.realizedPnlD6} /> : undefined} />;
    $[0] = color.hue;
    $[1] = color.text;
    $[2] = formatD6;
    $[3] = formatD6Signed;
    $[4] = href;
    $[5] = intent;
    $[6] = isOwnPortfolio;
    $[7] = label;
    $[8] = mode;
    $[9] = props;
    $[10] = row.market;
    $[11] = row.marketTitle;
    $[12] = row.outcomeImageNsfw;
    $[13] = row.outcomeImageUrl;
    $[14] = row.outcomeIndex;
    $[15] = row.realizedPnlD6;
    $[16] = row.resultState;
    $[17] = row.wallet;
    $[18] = t;
    $[19] = tFee;
    $[20] = t0;
  } else {
    t0 = $[20];
  }
  return t0;
}
function positionAmounts(props: PositionListItemProps): {
  realised: bigint | null;
  value: bigint | null;
  unrealised: bigint | null;
  invested: bigint | null;
  penalty: bigint | null;
} {
  const {
    row,
    mode
  } = props;
  const realised = moneyWire(row.realizedPnlD6);
  const value = props.mode === 'open' ? props.row.displayMark.currentValueD6 : null;
  const unrealised = props.mode === 'open' ? props.row.displayMark.unrealisedPnlD6 : null;
  const invested = moneyWire(mode === 'open' ? row.costBasisD6 : row.realizedCostBasisD6);
  const penalty = moneyWire(row.realizedPenaltyD6);
  return {
    realised,
    value,
    unrealised,
    invested,
    penalty
  };
}
function amount(value: bigint | null, format: (wire: string) => string, signed: boolean = false): ReactNode {
  if (value === null) return <Skeleton className="h-5 w-16" />;
  return <span className={signed ? pnlColorClass(value) : undefined}>
      <AnimatedBigInt value={value} duration={DURATION_POSITION_SMALL_MS} format={(current: bigint): string => format(current.toString())} />
    </span>;
}
function moneyWire(value: string): bigint | null {
  if (!/^-?(?:0|[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isI64(parsed) ? parsed : null;
}